/**
 * Normalize a date-of-birth string from common African / international formats
 * into the ISO `YYYY-MM-DD` format expected by the database.
 *
 * Supported inputs:
 *   YYYY-MM-DD  (passed through)
 *   DD/MM/YYYY, D/M/YYYY
 *   DD-MM-YYYY, D-M-YYYY
 *   DD.MM.YYYY, D.M.YYYY
 *   MM/DD/YYYY  (only when day > 12, so month position is unambiguous)
 *
 * Disambiguation: when both parts ≤ 12, DD/MM/YYYY is assumed (Africa-first).
 */

function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

function isValidDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > daysInMonth(month, year)) return false;
  if (year < 1900 || year > new Date().getFullYear()) return false;
  return true;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export function normalizeDateOfBirth(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Year-only (e.g., "1954") → YYYY-01-01
  if (/^\d{4}$/.test(trimmed)) {
    const y = parseInt(trimmed);
    if (y >= 1900 && y <= new Date().getFullYear()) return `${y}-01-01`;
    return '';
  }

  // Named month format: "19-Sep-24", "18-Apr-24", "4/Jun/2024", etc.
  const namedMonthMatch = trimmed.match(/^(\d{1,2})[/\-.]([A-Za-z]{3,})[/\-.](\d{2,4})$/);
  if (namedMonthMatch) {
    const monthNames: Record<string, number> = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12
    };
    const day = parseInt(namedMonthMatch[1]);
    const monthStr = namedMonthMatch[2].toLowerCase().slice(0, 3);
    let year = parseInt(namedMonthMatch[3]);
    const month = monthNames[monthStr];
    if (month) {
      if (year < 100) year = year >= 50 ? 1900 + year : 2000 + year;
      if (isValidDate(year, month, day)) return `${year}-${pad(month)}-${pad(day)}`;
    }
  }

  // Already ISO YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    return isValidDate(y, m, d) ? `${y}-${pad(m)}-${pad(d)}` : '';
  }

  // Split on / - or .
  const parts = trimmed.split(/[/\-.]/);
  if (parts.length !== 3) return '';

  const [a, b, c] = parts.map(Number);
  if ([a, b, c].some(isNaN)) return '';

  // Determine which part is the year (4-digit)
  let day: number, month: number, year: number;

  if (c >= 1900 && c <= new Date().getFullYear()) {
    // Format: ??/??/YYYY → DD/MM/YYYY (Africa-first) or MM/DD/YYYY
    // Africa-first: treat a=day, b=month
    if (isValidDate(c, b, a)) {
      day = a; month = b; year = c;
    } else if (isValidDate(c, a, b)) {
      // Fallback: a=month, b=day (US)
      day = b; month = a; year = c;
    } else {
      return '';
    }
  } else if (a >= 1900 && a <= new Date().getFullYear()) {
    // Format: YYYY/MM/DD
    year = a; month = b; day = c;
    if (!isValidDate(year, month, day)) return '';
  } else {
    return '';
  }

  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Year of birth only (e.g. "1976") for display OUTSIDE a patient's own
 * primary clinical record. Full day/month dates must never be shown there
 * (see Patient DOB Privacy standard). Returns "" when no year can be read.
 */
export function formatBirthYear(dob: string | Date | null | undefined): string {
  if (dob instanceof Date) {
    const y = dob.getFullYear();
    return y >= 1900 && y <= new Date().getFullYear() + 1 ? String(y) : '';
  }
  if (!dob) return '';
  const trimmed = String(dob).trim();
  if (!trimmed) return '';
  if (/^\d{4}(-\d{2})?(-\d{2})?/.test(trimmed)) return trimmed.slice(0, 4);
  const normalized = normalizeDateOfBirth(trimmed);
  if (normalized) return normalized.slice(0, 4);
  // Last resort: any plausible 4-digit year embedded in the text (e.g. "14 Mar 1976")
  const yearMatch = trimmed.match(/(19|20)\d{2}/);
  return yearMatch ? yearMatch[0] : '';
}
